/* eslint-disable lodash/prefer-lodash-method */

const _ = require('optimizely-client-fn');
const Api = require('./api');
const Action = require('core/action');
const Analytics = require('core/analytics');
const Layer = require('core/layer');
const View = require('core/view');
const Flux = require('core/flux');
const EventEmitter = require('core/event_emitter');
const EventEmitterEnums = require('core/constants/event_emitter');
const actionTypes = require('core/constants/action_types');
const PromiseEnv = require('core/lib/promise').Promise;
const ApiEvents = require('api/api_events');
const Logger = require('core/utils/logger');
const Cookie = require('core/utils/cookie');
const JSONEnv = require('core/lib/json');
const Controller = require('controller');
const Visitor = require('core/visitor');

const di = require('core/di');
const ActionDataStore = di.get('stores/action_data');
const DirectiveStore = di.get('stores/directive');
const ViewStore = di.get('stores/view');
const ViewDataStore = di.get('stores/view_data');
const GlobalStore = di.get('stores/global');
const VisitorIdStore = di.get('stores/visitor_id');
const LayerDataStore = di.get('stores/layer_data');
const EventDataStore = di.get('stores/event_data');
const VisitorBucketingStore = di.get('stores/visitor_bucketing');

function evaluatingPendingLayers(data) {

  const forceVariationIds = DirectiveStore.getForceVariationIds();
  const forceAudienceIds = DirectiveStore.getForceAudienceIds();

  return PromiseEnv.all[(data.queue.undecidedLayerIds || []).map(layerId => {

    const layer = LayerDataStore.get(layerId);
    const layerActiveViewsIds = ViewStore.getActiveViewStates()
      .map(view => view.id)
      .filter(viewId => layer.viewIds.includes(viewId));

    return Controller.decideAndExecuteLayerASAP(forceVariationIds, forceAudienceIds, layerActiveViewsIds, layer);
  })];

}

function executePendingChanges(data) {
    if ((data.queue.unappliedChangesetsIds || []).length === 0) {
        return;
    }

    const changesList: string[] = data.queue.unappliedChangesetsIds;
    let actionsData: ActionData[] = [];

    data.result.decisions.map(({layerId, experimentId, variationId, viewId}) => {

        const actionIdList = [
          `layerId:${layerId}`,
          `${experimentId}:${variationId}:${viewId}`
        ];

        for (const actionId of actionIdList) {
          const thisData: ActionData = ActionDataStore.get(actionId);

          if(!thisData || (thisData?.changeSet || []).length === 0){
            // No action data or no changeset, check next.
            continue;
          }

          thisData.changeSet = thisData.changeSet.filter((change: string | Change) => {
              var changeId: string = (_.isObject(change) ? (change as Change).id : change) as string;
              return changesList.includes(changeId);
          });

          actionsData = actionsData.concat(thisData);
        }
    });

    _.forEach(actionsData, Action.prepareAction);

    return PromiseEnv.all(_.map(actionsData, function(action:ActionData) {
        return Action.executePreparedAction(action)
          .then(_.partial(ApiEvents.emitActionAppliedEvent, action));
      })).then(function () {
        Logger.log('All pending page actions for applied:', actionsData);
      })['catch'](function (e) {
        Logger.warn('Error evaluating pending page actions for decision because:', e);
      });

}

function initializeVisitorData(data) {
  if (!data.metadata?.visitorData)
    return;

  if (data.metadata.visitorData.id) {
    Logger.log('API / Setting visitor Id from edge:', data.metadata.visitorData.id);
    Visitor.setId({randomId: data.metadata.visitorData.id});
  }

  loadWebSdkData();
}

function initializeViewStates(data) {

  let viewStates = [];

  (data.result?.activeViewsIds || []).forEach(viewId => {
      viewStates.push({id: viewId, isActive: true});
  });

  (data.result?.inactiveViewsIds || []).forEach(viewId => {
      viewStates.push({id: viewId, isActive: false});
  });

  View.registerViews(viewStates);
}

function setClientName() {
  Api.push({
    type: 'clientMetadata',
    clientName: 'ed-js',
  });
}


function emitResultEvents(data) {

  //  Emitting page view events
  (data.result?.activeViewsIds || []).forEach(viewId => {
    Api.push({
        type: 'event',
        eventType: 'pageview',
        eventData: {
            id: viewId,
            apiName: ViewDataStore.idToApiName(viewId)
        },
    });
  });

  var variationIdMap = VisitorBucketingStore.getVariationIdMap();
  var activationId = GlobalStore.getActivationId();
  var bucketingId = VisitorIdStore.getBucketingId();

  // Emitting edge delivery decisions
  (data.result?.decisions || [])
    .forEach(decision => {
      decision.isLayerHoldback = decision.isLayerHoldback || false;
      decision.isLayerConcluded = decision.isLayerConcluded || false;

      var decisionTicket:DecisionTicket = {
        activationId,
        bucketingId,
        audienceIds: [], // Empty because right now we don't have audienceIds in the decision
        globalHoldback: decision.isGlobalHoldback,
        preferredVariationMap: variationIdMap[decision.layerId] || {},
      };

      var layer = LayerDataStore.get(decision.layerId);
      Layer.recordLayerDecision(layer.id, decisionTicket, decision);
      Analytics.trackDecisionEvent(decision, decisionTicket, layer);
      ApiEvents.emitLayerDecided({layer, decisionTicket, decision});
    });
}

function printLogs(data) {

  if (!data.logs && !data.metadata?.logs) {
    return;
  }

  const allLogs = data.logs || data.metadata?.logs;

  Logger.groupCollapsed('Logs from Edge');

  // For some reason edge log levels are inverted
  (allLogs || []).forEach(log => {
    switch(log.level){
      case 1:
        Logger.debug(...['[EDGE] ' + log.message, ...log.rest]);
        break;
      case 2:
        Logger.log(...['[EDGE] ' + log.message, ...log.rest]);
        break;
      case 3:
        Logger.warn(...['[EDGE] ' + log.message, ...log.rest]);
        break;
      case 4:
        Logger.error(...['[EDGE] ' + log.message, ...log.rest]);
        break;
    }
  });

  Logger.groupEnd();
}

// Set up event tracking for active views
function registerEventImplementation(data) {
    const activeViews = data.result.activeViewsIds || [];
    activeViews.forEach(viewId => {
        const eventsToRegister = EventDataStore.getByPageId(viewId);
        View.toggleEventImplementations(eventsToRegister, true);
    });
}

function processWebSdk(data) {
    const onlyOneInitializeExecution = EventEmitter.on({
        filter: {
            type: EventEmitterEnums.TYPES.LIFECYCLE,
            name: 'initialized',
          },
        handler: () => {
            printLogs(data);
            initializeVisitorData(data);
            initializeViewStates(data);
            registerEventImplementation(data);
            setClientName();
            executePendingChanges(data);
            const onlyOneActivateExecution = EventEmitter.on({
              filter: {
                  type: EventEmitterEnums.TYPES.LIFECYCLE,
                  name: 'activate',
                },
              handler: () => {
                  // The decisions for this pending layers need the activationId
                  // moved process here.
                  evaluatingPendingLayers(data);
                  emitResultEvents(data);
                  EventEmitter.off(onlyOneActivateExecution);
              },
            });
            EventEmitter.off(onlyOneInitializeExecution);
        },
    });



}


const STICKY_STORAGE_KEY = {
  VISITOR_PROFILE: 'VPROF',
  VARIATION_MAP: 'VMAP',
};


/**
 * To reduce the size of the cookie, we use a custom transformer to encode the variation map.
 * The format is as follows:
 * layerId1_experimentId1_variationId1_layerId2_experimentId2_variationId2
 * For example:
 * 111_222_333_444_555_666_444_777_888
 * will be parsed as:
 * {
 *   111: {
 *     222: 333
 *   },
 *   444: {
 *     555: 666,
 *     777: 888
 *   }
 * }
 *
 * Obviously the stringify is the reverse of the parse.
 */
const VariationMapTransformer = {
  parse: function (vMapStr: string) {
    if(vMapStr.includes('{')) {
      // Detect if is json encoded
      return JSONEnv.parse(vMapStr);
    }

    const vMap = {};
    const vMapArr = vMapStr.split('_');
    for (let i = 0; i < vMapArr.length; i += 3) {
      const layerId = vMapArr[i];
      const experimentId = vMapArr[i + 1];
      const variationId = vMapArr[i + 2];
      if (!vMap[layerId]) {
        vMap[layerId] = {};
      }
      if (!vMap[layerId][experimentId]) {
        vMap[layerId][experimentId] = {};
      }
      vMap[layerId][experimentId] = variationId;
    }
    return vMap;
  },
  stringify: function (vMap: any) {
    const vMapArr = [];
    for (var layerId in vMap) {
      if (vMap.hasOwnProperty(layerId)) {
          for (var experiment in vMap[layerId]) {
              if (vMap[layerId].hasOwnProperty(experiment)) {
                vMapArr.push(layerId);
                vMapArr.push(experiment);
                vMapArr.push(vMap[layerId][experiment]);
              }
          }
      }
    }
    return vMapArr.join('_');
  }
};

/**
 * To reduce the size of the cookie, we use a custom transformer to encode the visitor profile.
 * The format is as follows:
 * attributeName1~~attributeValue1~~attributeMetadata1~~attributeName2~~attributeValue2~~attributeMetadata2
 *
 * If the attributeValue or attributeMetadata is a JSON object, it will be prefixed with '-*' and
 * the value will be JSON encoded.
 *
 * For example:
 * {
 *   "profile": {
 *    "attributeName1": "attributeValue1",
 *    "attributeName2": {"key":"value"},
 *   },
 *  "metadata": {
 *   "attributeName1": "attributeMetadata1",
 *   "attributeName2": {"foo":"bar"},
 *  }
 * }
 *
 * Will be parsed as:
 * attributeName1~~attributeValue1~~attributeMetadata1~~attributeName2~~-*{"key":"value"}~~-*{"foo":"bar"}
 */
const VisitorProfileTransformer = {
  parse: function (vProfStr: string) {
    if(vProfStr.startsWith('{')) {
      // Detect if is json encoded
      return JSONEnv.parse(vProfStr);
    }

    const JSON_PREFIX = '-*';
    const SEPARATOR = '~~';

    const vProfile = {
      profile: {},
      metadata: {}
    };
    const vProfArr = vProfStr.split(SEPARATOR);
    for (let i = 0; i < vProfArr.length; i += 3) {
      const attributeName = vProfArr[i];
      const attributeValue = vProfArr[i + 1];
      const attributeMetadata = vProfArr[i + 2];

      if (attributeValue.startsWith(JSON_PREFIX)) {
        vProfile.profile[attributeName] = JSONEnv.parse(attributeValue.substring(2));
      } else {
        vProfile.profile[attributeName] = attributeValue;
      }
      if (attributeMetadata.startsWith(JSON_PREFIX)) {
        vProfile.metadata[attributeName] = JSONEnv.parse(attributeMetadata.substring(2));
      } else {
        vProfile.metadata[attributeName] = attributeMetadata;
      }
    }
    return vProfile;
  },
  stringify: function (vProf: any) {
    const vProfArr = [];
    const JSON_PREFIX = '-*';
    const SEPARATOR = '~~';

    for (var attributeName in vProf['profile']) {
      if (vProf['profile'].hasOwnProperty(attributeName)) {

          // TODO: Check if the value could be an undefined.
          let attributeValue = vProf['profile'][attributeName];
          let attributeMetadata = vProf['metadata'][attributeName];

          if ( typeof attributeValue === 'object' ) {
              attributeValue = JSON_PREFIX + JSONEnv.stringify(attributeValue);
          }

          if ( typeof attributeMetadata === 'object' ) {
              attributeMetadata = JSON_PREFIX + JSONEnv.stringify(attributeMetadata);
          }

          vProfArr.push(attributeName);
          vProfArr.push(attributeValue);
          vProfArr.push(attributeMetadata);
      }
    }

    return vProfArr.join(SEPARATOR);
  }
};


export class StickyProcesor {
    public static readonly SEPARATOR: string = '$$';

    visitorId: string;
    namespace: string;
    cookies: Record<string, string>;

    transformers: Record<string, any> = {
        [STICKY_STORAGE_KEY.VARIATION_MAP]: VariationMapTransformer,
        [STICKY_STORAGE_KEY.VISITOR_PROFILE]: VisitorProfileTransformer,
    };

    constructor(
        visitorId: string,
        namespace: string,
        cookies: Record<string, string> | undefined
    ) {
        this.visitorId = visitorId;
        this.namespace = namespace;
        this.cookies = cookies || {};
    }

    getKey(keyName: string): string {
        return ['OPTY', this.visitorId, this.namespace, keyName].join(
            StickyProcesor.SEPARATOR
        );
    }

    getStringValue(cookieName: string): string {
        return this.cookies[cookieName] || '{}';
    }

    getValue(storage_key: string): any {
        const cookieKey = this.getKey(storage_key);
        const value = this.getStringValue(cookieKey);
        const transformer = this.transformers[storage_key] || JSONEnv;
        return value ? transformer.parse(value) : null;
    }

    setValue(storage_key: string, value: any): void {
        const cookieKey = this.getKey(storage_key);
        const transformer = this.transformers[storage_key] || JSONEnv;
        Cookie.set(cookieKey, transformer.stringify(value));
    }
}

function getStickyProcessor(){
  return new StickyProcesor(
    VisitorIdStore.getRandomId(),
    GlobalStore.getNamespace(),
    Cookie.getAll()
  );
}


function loadWebSdkData() {
  const processor = getStickyProcessor();

  let valueVariationMap = processor.getValue(STICKY_STORAGE_KEY.VARIATION_MAP);
  if (valueVariationMap) {
    Flux.dispatch(actionTypes.MERGE_VARIATION_ID_MAP, {
      variationIdMap: valueVariationMap,
    });
  }

  let valueVisitorProfile = processor.getValue(STICKY_STORAGE_KEY.VISITOR_PROFILE);
  if (valueVisitorProfile) {
    Flux.dispatch(actionTypes.LOAD_EXISTING_VISITOR_PROFILE, {
      profile: valueVisitorProfile.profile || {},
      metadata: valueVisitorProfile.metadata || {},
    });
  }
}


function getCurrentStickyEdgeData(key:string): any {
  const processor = getStickyProcessor();
  return processor.getValue(key);
}

function setCurrentStickyEdgeData(key:string, value: any) {
  const processor = getStickyProcessor();
  processor.setValue(key, value);
}

module.exports = {
  STICKY_STORAGE_KEY,
  processWebSdk,
  getCurrentStickyEdgeData,
  setCurrentStickyEdgeData
};
